<template>
  <form-layout :title="`${label} Skipped`" bottom-bar-stick-bottom @on-dismiss="dismiss">
    <vs-wrapper>
      <vs-heading
        v-if="requestedStop"
        class="d-flex heading-overline"
        type="overline"
        data-test="requested-stop-heading"
        >Requested {{ label }}
      </vs-heading>
      <v-list v-if="requestedStop" class="pa-0" data-test="requested-stop-items">
        <v-list-item v-for="item in requestedStopListItems" :key="item.key" class="pl-0 pt-0">
          <v-list-item-icon>
            <v-icon>{{ item.icon }}</v-icon>
          </v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title>{{ item.title }}</v-list-item-title>
            <v-list-item-subtitle
              v-for="(subtitle, index) in item.subtitles"
              :key="item.key + '-' + index"
              :data-test="`requested-stop-item-${item.key}-${index}`"
            >
              {{ subtitle }}
            </v-list-item-subtitle>
          </v-list-item-content>
        </v-list-item>
      </v-list>
      <vs-heading class="d-flex heading-overline" type="overline" data-test="receipt-heading"
        >Skip Confirmation
      </vs-heading>
      <v-list class="pa-0">
        <v-list-item v-for="item in actualStopListItems" :key="item.key" class="pl-0 pt-0">
          <v-list-item-icon>
            <v-icon>{{ item.icon }}</v-icon>
          </v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title>{{ item.title }}</v-list-item-title>
            <v-list-item-subtitle
              v-for="(subtitle, index) in item.subtitles"
              :key="item.key + '-' + index"
              :data-test="`receipt-item-${item.key}-${index}`"
              >{{ subtitle }}</v-list-item-subtitle
            >
          </v-list-item-content>
        </v-list-item>
      </v-list>
    </vs-wrapper>
    <template #action-bar>
      <vs-wrapper direction="horizontal">
        <disabled-action-tooltip :permission="unskipPermission" label="Unskip" full-width>
          <template #default="{ disabled }">
            <vs-button
              :disabled="disabled"
              :loading="isDeletingActualStop"
              icon="mdi-delete"
              label="Unskip"
              type="primary"
              destructive
              full-width
              data-test="unskip-button"
              @click="deleteClick"
            />
          </template>
        </disabled-action-tooltip>
      </vs-wrapper>
    </template>
  </form-layout>
</template>
<script setup lang="ts">
import { computed, ref, toRef, unref } from 'vue'
import FormLayout from '@/components/common/FormLayout.vue'
import VsWrapper from '@/components/vision/VsWrapper.vue'
import VsHeading from '@/components/vision/VsHeading.vue'
import VsButton from '@/components/vision/VsButton.vue'
import DisabledActionTooltip from '@/components/common/DisabledActionTooltip.vue'
import { allOf, check, usePermission } from '@/components/common/permission'
import { disabledActionMessages as message } from './disabledActionMessages'

import { useJobBottomSheetStatesStore } from '@/components/jobs/jobBottomSheetStatesStore'

import { useGetJobRequestedStopsQuery, useGetJobStopCommentsQuery } from '@/api/jobs'
import { type InventoryItem, JobStopType } from '@ironsight/types'
import { buildDisplayInventory, buildInventoryDisplayString } from '@/utils/inventories'
import { useInventoryTypes } from '@/composables/inventory-types/useInventoryTypes'
import { useDeleteActualStopMutation } from '@/api/composables/jobs/stops'

import { useStore } from 'vuex-composition-helpers'
import User from '@/models/user'
import { useQueryClient } from '@tanstack/vue-query'
import { useJobStopsAndReceiptsPermissions } from './useJobStopsAndReceiptsPermissions'
import { useSuggestedInventory } from '@/components/tickets/StopsAndInventories/useInventoryReceiptSuggestedInventory'

const jobBottomSheetStatesStore = useJobBottomSheetStatesStore()
const props = defineProps<{
  jobId: string
  jobNumber: number
}>()

const jobIdRef = toRef(props, 'jobId')
const jobNumberRef = toRef(props, 'jobNumber')
const { data: requestedStops } = useGetJobRequestedStopsQuery(jobIdRef)
const { data: stopComments } = useGetJobStopCommentsQuery(jobIdRef)
const { mutateAsync: deleteActualStop, isLoading: isDeletingActualStop } =
  useDeleteActualStopMutation()

const store = useStore()
const queryClient = useQueryClient()

const actualStop = computed(
  () =>
    jobBottomSheetStatesStore.jobBottomSheetStates.actualSkippedStopDetailsBottomSheetStates
      .actualStop,
)
const isPickupStop = computed(() => actualStop.value?.stopType === JobStopType.PickUp)
const label = computed(() => (isPickupStop.value ? 'Pickup' : 'Dropoff'))

const { suggestedInventory } = useSuggestedInventory(
  jobIdRef,
  actualStop.value?.requestedStopId ?? null,
  actualStop.value?.stopType ?? JobStopType.DropOff,
)

const requestedStop = computed(() =>
  requestedStops.value?.find(
    (requestedStop) => requestedStop.id === actualStop.value?.requestedStopId,
  ),
)

const requestedStopInventory = computed(() => {
  if (!requestedStop.value) {
    return []
  } else if (requestedStop.value.stopType === JobStopType.PickUp) {
    return requestedStop.value.inventory
  } else {
    return suggestedInventory.value
  }
})

const createdBySid = computed(() => actualStop.value?.createdBy ?? null)

const userBySid = computed<(userSid: string) => User | null | undefined>(
  () => store.getters['users/getBySid'],
)

const createdByUser = computed(() =>
  createdBySid.value ? userBySid.value(createdBySid.value) : null,
)
const { isJobCompleted, createStopsPermission } = useJobStopsAndReceiptsPermissions(
  jobNumberRef,
  ref(null),
)
const { permission: unskipPermission, allowed: canUnskip } = usePermission(() =>
  allOf(
    createStopsPermission.value,
    check(!isJobCompleted.value, message.completed),
    check(!unref(isDeletingActualStop), message.deleting),
  ),
)

const locationItem = (locationName: string | null | undefined) => ({
  key: 'location',
  icon: 'mdi-map-marker-outline',
  title: 'Location',
  subtitles: [locationName],
})

const { allInventoryTypes } = useInventoryTypes()

const inventoryItem = (inventory: InventoryItem[]) => {
  const displayInventory = buildDisplayInventory(inventory, allInventoryTypes.value ?? []).map(
    buildInventoryDisplayString,
  )

  return {
    key: 'inventoryItem',
    icon: 'mdi-package-variant-closed',
    title: 'Item',
    subtitles: displayInventory,
  }
}

const actualStopListItems = computed(() => [
  {
    key: 'skippedBy',
    icon: 'mdi-account-circle-outline',
    title: 'Skipped By',
    subtitles: [createdByUser.value?.name ?? 'Unknown User'],
  },
  {
    key: 'comments',
    icon: 'mdi-comment-text-outline',
    title: "Operator's Comments",
    subtitles: stopComments.value
      ?.filter((comment) => comment.reason === 'Skip' && comment.stopId === actualStop.value?.id)
      .map((c) => c.comment),
  },
  {
    key: 'date',
    icon: 'mdi-calendar-outline',
    title: 'Date',
    subtitles: [actualStop.value?.arrivedAt.format('LLLL')],
  },
])

const requestedStopListItems = computed(() => {
  return [
    locationItem(requestedStop.value?.locationName),
    inventoryItem(requestedStopInventory.value ?? []),
  ]
})

const dismiss = () => {
  jobBottomSheetStatesStore.clearJobBottomSheetStates()
}

const deleteClick = async () => {
  if (!canUnskip.value || !actualStop.value?.id) {
    return
  }

  const shouldDelete = window.confirm('Are you sure you want to delete this stop?')
  if (!shouldDelete) {
    return
  }

  try {
    await deleteActualStop({
      jobId: props.jobId,
      stopId: actualStop.value.id,
    })

    queryClient.invalidateQueries(['history', 'byJobNumber', props.jobNumber])

    dismiss()
  } catch {
    // Keep the bottom sheet open if the delete fails.
  }
}
</script>

<style scoped>
.v-list-item__subtitle {
  white-space: normal;
  overflow-wrap: anywhere;
}
</style>
